import {
  NativeValidityStateKeys,
  StratoValidityStateKeys,
  ValidationError,
  ValidityStateKey,
} from './types.js';
import type {
  ValidationErrorSource,
  ValidationState,
  ValidationTrigger,
} from './types.js';

type NativeValidationElement =
  | HTMLInputElement
  | HTMLSelectElement
  | HTMLTextAreaElement
  | null
  | undefined;

type ValidationStateInput = NativeValidationElement | ValidityStateKey;

function getValidityStateKeySource(
  validityStateKey: ValidityStateKey,
): ValidationErrorSource {
  if (
    Object.values(StratoValidityStateKeys).some((key) =>
      key.equals(validityStateKey),
    )
  ) {
    return 'strato';
  }

  if (
    Object.values(NativeValidityStateKeys).some((key) =>
      key.equals(validityStateKey),
    )
  ) {
    return 'native';
  }

  return 'custom';
}

/**
 * Creates a {@link ValidationState} from a source-specific validation result.
 *
 * @remarks
 * When passing a native element, the caller is responsible for triggering
 * validation (e.g. `checkValidity()`) before calling this. Validity keys are
 * classified by comparing against the exported Native and Strato key registries;
 * unknown keys are treated as custom validation errors.
 *
 * @internal
 */
export function getValidationState(
  input: ValidationStateInput,
  trigger: ValidationTrigger,
  message?: string,
  formControlId?: string | string[],
): ValidationState {
  if (!input) {
    return { status: 'done', trigger, errors: [] };
  }

  if (input instanceof ValidityStateKey) {
    const validityStateKey = input;
    const source = getValidityStateKeySource(validityStateKey);

    return {
      status: 'done',
      trigger,
      errors: [
        new ValidationError(message ?? validityStateKey.value, {
          source,
          validityStateKey,
          formControlId,
        }),
      ],
    };
  }

  const element = input;

  if (element.validity.valid) {
    return { status: 'done', trigger, errors: [] };
  }

  const resolvedMessage = message ?? element.validationMessage;
  const errors = Object.values(NativeValidityStateKeys)
    .filter((key) => element.validity[key.value])
    .map(
      (key) =>
        new ValidationError(resolvedMessage, {
          source: 'native',
          validityStateKey: key,
          formControlId: element.id || undefined,
        }),
    );

  return { status: 'done', trigger, errors };
}
